import { useState, useEffect, useCallback, useRef } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { ProColumns } from "@ant-design/pro-components";
import { useTranslation } from "react-i18next";
import { Radio } from "antd";
import BasicTable, { PropsRef } from "../BasicTable";
import { FilterParams } from "@/facilities/model/user";
import { usePolling, useUrlPath } from "@/hooks";
import {
  DEPLOYMENT,
  OUTSTANDING,
  CLOSED,
  FAULT_TABLESTATUS,
  EIMIT,
} from "@/enums/pageEnum";

export default function ParentPonent({
  tableColsRender,
  filterTableFields,
  editTableHeaderUrl,
  requestUrl,
  tableName,
  prantPath,
  countUrl,
  tableTitle,
}: {
  tableColsRender?: ProColumns[];
  filterTableFields: FilterParams[];
  editTableHeaderUrl?: string;
  requestUrl?: string;
  tableName?: string;
  prantPath?: string;
  countUrl: string;
  tableTitle?: string;
}) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const navigation = useNavigate();
  const { deleteNameToUrl, updateNameToUrl } = useUrlPath();
  const [groupParams, setGroupParams] = useState<string>("");
  const tableRef = useRef<PropsRef>(null);
  const [isPolling, setIsPolling] = useState<boolean>(false);

  const { data, fetchData, stopPolling, startPolling, originParams } =
    usePolling({
      url: countUrl,
      excludeParams: ["date", "operationalStatus"],
      updateKey: { dateStart: "startCreateTs", dateEnd: "endCreateTs" },
    });

  useEffect(() => {
    if (originParams) {
      const key = originParams[FAULT_TABLESTATUS];
      setGroupParams(key);
    }
  }, [originParams]);

  const menuItemOnClick = useCallback(() => {
    setGroupParams("");
  }, []);

  useEffect(() => {
    document.addEventListener(EIMIT, menuItemOnClick);
    return () => {
      document.removeEventListener(EIMIT, menuItemOnClick);
    };
  }, [menuItemOnClick]);

  useEffect(() => {
    if (pathname !== prantPath) {
      stopPolling();
      setIsPolling(false);
    } else {
      setIsPolling(true);
      startPolling();
    }
  }, [pathname, prantPath, stopPolling, startPolling]);

  const callBackFunc = (isReach?: boolean) => {
    if (isReach) {
      navigation(0);
    }
    fetchData();
    tableRef.current?.afreshTableData();
    setIsPolling(true);
  };

  return (
    <>
      <div className="p-4">
        <BasicTable
          tableColsRender={tableColsRender}
          isShowCheckbox={false}
          filterTableFields={filterTableFields}
          filterSubmit={() => {
            fetchData();
          }}
          resetSubmit={() => {
            fetchData({}, false);
          }}
          ref={tableRef}
          tableName={tableName}
          editTableHeaderUrl={editTableHeaderUrl}
          requestUrl={requestUrl}
          headerTitle={() => {
            return (
              <>
                <span>{tableTitle}</span>
                <Radio.Group
                  onChange={({ target }) => {
                    const val = target.value;
                    if (val === "ALL") {
                      navigation(deleteNameToUrl(FAULT_TABLESTATUS));
                    } else {
                      navigation(updateNameToUrl(FAULT_TABLESTATUS, val));
                    }
                    fetchData();
                    setGroupParams(val);
                    tableRef.current?.afreshTableData({ page: 1 });
                  }}
                  value={groupParams}
                  className="font-normal liftFaultClass"
                >
                  <Radio.Button value="ALL">
                    {t("all")} {data?.total}
                  </Radio.Button>
                  <Radio.Button value={DEPLOYMENT}>
                    {t("deployment")} {data?.deploymentTotal}
                  </Radio.Button>
                  <Radio.Button value={OUTSTANDING}>
                    {t("outstanding")} {data?.outstandingTotal}
                  </Radio.Button>
                  <Radio.Button value={CLOSED}>
                    {t("closed")} {data?.closedTotal}
                  </Radio.Button>
                </Radio.Group>
              </>
            );
          }}
          dateKeys={{ dateStart: "startCreateTs", dateEnd: "endCreateTs" }}
          className="min-h-[calc(100vh-217px)]"
          longPolling={isPolling}
        />
      </div>
      {pathname !== prantPath && <Outlet context={{ callBackFunc }} />}
    </>
  );
}
